import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import ProgressionBuilderNodeView from './progression-builder-node-view';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        progressionBuilder: {
            insertProgressionBuilder: (options?: InsertProgressionBuilderOptions) => ReturnType;
        };
    }
}

export interface InsertProgressionBuilderOptions {
    name?: string;
    caption?: string;
    progressionXMLToLoad?: string;
    options?: { [key: string]: any };
    alt_text?: string;
    instructions?: string;
    challenge?: boolean;
    parts?: number;
    optional?: boolean;
    image_ids?: string[];
    maImageNodeIds?: Record<string, string>;
}

export const ProgressionBuilderNodeHeadless = Node.create({
    name: 'progressionBuilder',

    group: 'block',

    atom: true,

    selectable: true,

    draggable: true,

    addAttributes() {
        return {
            name: {
                default: '',
                parseHTML: element => element.getAttribute('data-tool-name') || '',
                renderHTML: attributes => ({
                    'data-tool-name': attributes.name,
                }),
            },
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-progression-id'),
                renderHTML: attributes => ({
                    'data-progression-id': attributes.id,
                }),
            },
            caption: {
                default: '',
                parseHTML: element => element.getAttribute('data-caption') || '',
                renderHTML: attributes => ({
                    'data-caption': attributes.caption,
                }),
            },
            progressionXMLToLoad: {
                default: '',
                parseHTML: element => element.getAttribute('data-progression-xml') || '',
                renderHTML: attributes => ({
                    'data-progression-xml': attributes.progressionXMLToLoad,
                }),
            },
            options: {
                default: {},
                parseHTML: element => {
                    const raw = element.getAttribute('data-options');
                    try {
                        return raw ? JSON.parse(raw) : {};
                    } catch (error) {
                        console.error('Failed to parse data-options:', error, raw);
                        return {};
                    }
                },
                renderHTML: attributes => ({
                    'data-options': JSON.stringify(attributes.options),
                }),
            },
            alt_text: {
                default: '',
                parseHTML: element => element.getAttribute('data-alt-text') || '',
                renderHTML: attributes => ({
                    'data-alt-text': attributes.alt_text,
                }),
            },
            instructions: {
                default: '',
                parseHTML: element => element.getAttribute('data-instructions') || '',
                renderHTML: attributes => ({
                    'data-instructions': attributes.instructions,
                }),
            },
            challenge: {
                default: false,
                parseHTML: element => element.getAttribute('data-challenge') === 'true',
                renderHTML: attributes => ({
                    'data-challenge': attributes.challenge ? 'true' : 'false',
                }),
            },
            parts: {
                default: 1,
                parseHTML: element => parseInt(element.getAttribute('data-parts') || '1', 10),
                renderHTML: attributes => ({
                    'data-parts': attributes.parts,
                }),
            },
            optional: {
                default: false,
                parseHTML: element => element.getAttribute('data-optional') === 'true',
                renderHTML: attributes => ({
                    'data-optional': attributes.optional ? 'true' : 'false',
                }),
            },
            image_ids: {
                default: [],
                parseHTML: element => {
                    const ids = element.getAttribute('data-image-ids');
                    return ids && ids.trim()
                        ? ids
                              .split(',')
                              .map(id => id.trim())
                              .filter(id => id)
                        : [];
                },
                renderHTML: attributes => ({
                    'data-image-ids': attributes.image_ids?.join(',') || '',
                }),
            },
            maImageNodeIds: {
                default: {},
                parseHTML: element => {
                    try {
                        return JSON.parse(element.getAttribute('data-ma-image-node-ids') || '{}');
                    } catch {
                        return {};
                    }
                },
                renderHTML: attributes => ({
                    'data-ma-image-node-ids': JSON.stringify(attributes.maImageNodeIds || {}),
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="progression-builder"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'progression-builder',
                class: 'progression-builder-container',
            }),
        ];
    },

    addCommands() {
        return {
            insertProgressionBuilder:
                (options: InsertProgressionBuilderOptions = {}) =>
                ({ commands }) => {
                    const defaultProgressionXML = `<zyTool name="ProgressionPlayerInteractiveAndAccessible" caption="" id="c33d499c-3f79-40bc-b58f-63211bc5e683" challenge="true" parts="1" optional="false" edited-last="Mon Dec 01 2025 16:18:00 GMT-0800 (Pacific Standard Time)" image-ids="">
<zyInstructions></zyInstructions><zyOptions><progression type="dict">
<code type="string"></code>
<elements type="list"></elements><levels type="list"><item type="dict">
<code type="string"></code>
<elementVariants type="list"></elementVariants><questions type="list"><item type="dict">
<code type="string"></code>
<elementVariants type="list"></elementVariants>
<explanation type="string"></explanation>
<height type="string"></height><id type="string">ea96eb04-1a1c-4ef3-96fd-b3b3393812c3</id><isSelected type="boolean">true</isSelected><name type="string">Question a</name><usedElements type="list"></usedElements><width type="string"></width></item></questions>
<explanation type="string"></explanation>
<height type="string">350px</height><id type="string">5a3be5a4-fdf7-4456-9cc1-df8af05f837f</id><isCollapsed type="boolean">true</isCollapsed><isSelected type="boolean">true</isSelected><name type="string">Level 1</name><usedElements type="list"></usedElements><useMarkdownInExplanation type="boolean">false</useMarkdownInExplanation><width type="string">550px</width></item></levels>
<explanation type="string"></explanation>
<height type="string"></height><mathjax2 type="boolean">false</mathjax2><useMarkdownInTextElements type="boolean">false</useMarkdownInTextElements><width type="string"></width><widthLocked type="boolean">false</widthLocked><isExam type="boolean">false</isExam></progression></zyOptions><zyAltDescription></zyAltDescription></zyTool>`;

                    return commands.insertContent({
                        type: this.name,
                        attrs: {
                            name: options.name || '',
                            caption: options.caption || '',
                            progressionXMLToLoad: options.progressionXMLToLoad || defaultProgressionXML,
                            options: options.options || {},
                            alt_text: options.alt_text || '',
                            instructions: options.instructions || '',
                            challenge: options.challenge || false,
                            parts: options.parts || 1,
                            optional: options.optional || false,
                            image_ids: options.image_ids || [],
                            maImageNodeIds: options.maImageNodeIds || {},
                        },
                    });
                },
        };
    },
});

export const ProgressionBuilderNode = ProgressionBuilderNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ProgressionBuilderNodeView);
    },
});

export default ProgressionBuilderNode;
